import React, { FC, useState, useMemo, memo } from 'react';
import _map from 'lodash/map';
import _take from 'lodash/take';
import { CoreTheme as theme } from '@minecraft.themes';
import { useTranslation } from '@blocs.i18n';
import { PreparePillsResult } from '../../shared/utils';
import { ClearButton } from '../../shared/styles';
import {
  Container,
  PillList,
  HiddenPillsCount,
  ButtonGroup,
  ExpandButton,
  ExpandIcon,
} from './FilterOverlayBreadboxStyles';
import FilterOverlayBreadboxPill from './FilterOverlayBreadboxPill';

const VISIBLE_PILLS_LENGTH_BY_DEFAULT = 3;

type Props = {
  pills: PreparePillsResult;
  onClear: (filterKey?: string) => void;
};

const FilterOverlayBreadbox: FC<Props> = ({ pills, onClear }) => {
  const { t } = useTranslation();
  const [isExpanded, setExpanded] = useState(false);

  const pillsKeys = Object.keys(pills);
  const visiblePillsKeys = useMemo(
    () => (isExpanded ? pillsKeys : _take(pillsKeys, VISIBLE_PILLS_LENGTH_BY_DEFAULT)),
    [isExpanded, pills]
  );
  const hiddenPillsCount = pillsKeys.length - visiblePillsKeys.length;

  const toggleBreadbox = () => {
    setExpanded(!isExpanded);
  };

  return (
    <Container $isExpanded={isExpanded}>
      <PillList>
        {_map(visiblePillsKeys, (key) => (
          <FilterOverlayBreadboxPill
            key={key}
            pillKey={key}
            pill={pills[key]}
            handleClear={(): void => {
              onClear(key);
            }}
          />
        ))}
        {!isExpanded && hiddenPillsCount > 0 && (
          <HiddenPillsCount onClick={toggleBreadbox}>
            +{hiddenPillsCount} {t('common:label.more')}
          </HiddenPillsCount>
        )}
      </PillList>
      <ButtonGroup>
        {!isExpanded && (
          <ClearButton
            onClick={(): void => {
              onClear();
            }}
          >
            {t('common:button.clear')}
          </ClearButton>
        )}
        {pillsKeys.length > VISIBLE_PILLS_LENGTH_BY_DEFAULT && (
          <ExpandButton>
            <ExpandIcon
              name={isExpanded ? 'chevron-open-thin' : 'chevron-closed-thin'}
              color={theme.colors.primary.darkGrey}
              onClick={toggleBreadbox}
            />
          </ExpandButton>
        )}
      </ButtonGroup>
    </Container>
  );
};

export default memo<Props>(FilterOverlayBreadbox);
